import { useState, useEffect, useRef, useCallback } from 'react';

// ── Icons ──────────────────────────────────────────────────────
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);
const StopIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
  </svg>
);
const CameraIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);
const MapPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const FootprintIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5c0 1.93 1 3.8 1 5.5V16"/><path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9c0 1.93-1 3.8-1 5.5V20"/>
    <path d="M4 17a4 4 0 0 0 4 3 4 4 0 0 0 4-3"/><path d="M14 21a4 4 0 0 0 4 3 4 4 0 0 0 4-3"/>
  </svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4h6v2"/>
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

// ── Stat Card ──────────────────────────────────────────────────
function StatCard({ icon, label, value, color }) {
  return (
    <div style={{
      background: 'var(--pg-surface2)',
      border: '1px solid var(--pg-border)',
      borderRadius: '12px',
      padding: '12px 14px',
      display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: `${color}18`,
        border: `1px solid ${color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '11px', color: 'var(--pg-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {label}
        </div>
        <div style={{ fontSize: '18px', fontFamily: "'Syne', sans-serif", fontWeight: 700, color: 'var(--pg-text)', lineHeight: 1.2 }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ── Map Component (Leaflet via CDN) ───────────────────────────
function RondeMap({ positions, isTracking }) {
  const mapRef    = useRef(null);
  const mapObj    = useRef(null);
  const markerRef = useRef(null);
  const polyRef   = useRef(null);
  const loadedRef = useRef(false);

  const initMap = useCallback(() => {
    if (!mapRef.current || mapObj.current) return;
    if (!window.L) return;

    const L = window.L;
    const defaultPos = [36.8065, 10.1815]; // Tunis par défaut

    const map = L.map(mapRef.current, {
      center: defaultPos,
      zoom: 17,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    mapObj.current = map;

    // Marker position actuelle
    const pulseIcon = L.divIcon({
      className: '',
      html: `<div style="
        width:18px;height:18px;border-radius:50%;
        background:#3b9eff;border:3px solid #fff;
        box-shadow:0 0 0 4px rgba(59,158,255,0.3);
      "></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });

    markerRef.current = L.marker(defaultPos, { icon: pulseIcon }).addTo(map);

    // Polyline du trajet
    polyRef.current = L.polyline([], {
      color: '#3b9eff',
      weight: 3,
      opacity: 0.8,
      dashArray: '6 4',
    }).addTo(map);
  }, []);

  // Charger Leaflet
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(css);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      setTimeout(initMap, 100);
    };
    document.head.appendChild(script);

    return () => {};
  }, [initMap]);

  // Mettre à jour la carte quand les positions changent
  useEffect(() => {
    if (!mapObj.current || !window.L || positions.length === 0) return;
    const L = window.L;
    const last = positions[positions.length - 1];
    const latLng = [last.lat, last.lng];

    // Déplacer le marker
    if (markerRef.current) markerRef.current.setLatLng(latLng);

    // Mettre à jour le polyline
    if (polyRef.current) {
      const latlngs = positions.map(p => [p.lat, p.lng]);
      polyRef.current.setLatLngs(latlngs);
    }

    // Centrer la carte
    if (isTracking) mapObj.current.panTo(latLng);

    // Ajouter un point de trajet
    if (positions.length > 1) {
      const prev = positions[positions.length - 2];
      L.circleMarker([prev.lat, prev.lng], {
        radius: 4,
        fillColor: '#3b9eff',
        color: '#fff',
        weight: 1,
        fillOpacity: 0.8,
      }).addTo(mapObj.current);
    }
  }, [positions, isTracking]);

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%', height: '320px',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid var(--pg-border-md)',
        background: '#0a1929',
      }}
    />
  );
}

// ── Photo Gallery ──────────────────────────────────────────────
function PhotoGallery({ photos, onDelete }) {
  if (photos.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--pg-text-muted)', marginBottom: '8px' }}>
        Photos prises ({photos.length})
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '8px' }}>
        {photos.map((p, i) => (
          <div key={i} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--pg-border)' }}>
            <img src={p.url} alt={`Photo ${i + 1}`} style={{ width: '100%', height: '90px', objectFit: 'cover', display: 'block' }} />
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(4,13,26,0.75)', padding: '3px 5px', fontSize: '9px', color: '#7a9cc4' }}>
              {p.time}
            </div>
            <button
              onClick={() => onDelete(i)}
              style={{
                position: 'absolute', top: '4px', right: '4px',
                width: '20px', height: '20px',
                background: 'rgba(239,68,68,0.8)', border: 'none',
                borderRadius: '50%', color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Écran d'accueil Ronde ─────────────────────────────────────
function RondeAccueil({ onStart, onPhotoOnly }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1.5rem', gap: '1.5rem', textAlign: 'center' }}>

      {/* Icône centrale animée */}
      <div style={{
        width: 80, height: 80, borderRadius: '50%',
        background: 'rgba(59,158,255,0.08)',
        border: '2px solid rgba(59,158,255,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'rondeGlow 2.5s ease-in-out infinite',
      }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="#3b9eff" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" width="36" height="36">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>

      <div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1.2rem', fontWeight: 700, color: 'var(--pg-text)', marginBottom: '6px' }}>
          Mode Ronde
        </div>
        <div style={{ fontSize: '13px', color: 'var(--pg-text-sec)', lineHeight: 1.6, maxWidth: '320px' }}>
          Choisissez votre mode de ronde. La géolocalisation enregistre votre trajet en temps réel.
        </div>
      </div>

      {/* Deux options */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', width: '100%', maxWidth: '420px' }}>

        {/* Option 1 — Géolocalisation */}
        <button
          onClick={onStart}
          style={{
            padding: '20px 14px',
            background: 'rgba(59,158,255,0.08)',
            border: '1px solid rgba(59,158,255,0.25)',
            borderRadius: '16px',
            cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
            transition: 'all 0.2s',
            color: 'var(--pg-blue)',
          }}
          onMouseOver={e => e.currentTarget.style.background = 'rgba(59,158,255,0.15)'}
          onMouseOut={e => e.currentTarget.style.background = 'rgba(59,158,255,0.08)'}
        >
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(59,158,255,0.12)', border: '1px solid rgba(59,158,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
          <div>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '13px', fontWeight: 700, marginBottom: '3px' }}>
              Suivi GPS
            </div>
            <div style={{ fontSize: '11px', color: 'var(--pg-text-muted)', lineHeight: 1.4 }}>
              Trajet + map en temps réel
            </div>
          </div>
        </button>

        {/* Option 2 — Photos seulement */}
        <button
          onClick={onPhotoOnly}
          style={{
            padding: '20px 14px',
            background: 'rgba(245,166,35,0.08)',
            border: '1px solid rgba(245,166,35,0.25)',
            borderRadius: '16px',
            cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
            transition: 'all 0.2s',
            color: 'var(--pg-amber)',
          }}
          onMouseOver={e => e.currentTarget.style.background = 'rgba(245,166,35,0.15)'}
          onMouseOut={e => e.currentTarget.style.background = 'rgba(245,166,35,0.08)'}
        >
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(245,166,35,0.12)', border: '1px solid rgba(245,166,35,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
          </div>
          <div>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '13px', fontWeight: 700, marginBottom: '3px' }}>
              Photos seules
            </div>
            <div style={{ fontSize: '11px', color: 'var(--pg-text-muted)', lineHeight: 1.4 }}>
              Sans suivi de position
            </div>
          </div>
        </button>
      </div>

      <style>{`
        @keyframes rondeGlow {
          0%,100% { box-shadow: 0 0 0 0 rgba(59,158,255,0); }
          50% { box-shadow: 0 0 0 12px rgba(59,158,255,0.08); }
        }
      `}</style>
    </div>
  );
}

// ── VueRonde — Composant principal ────────────────────────────
export default function VueRonde() {

  // États généraux
  const [mode,      setMode]      = useState('accueil'); // 'accueil' | 'gps' | 'photo'
  const [statut,    setStatut]    = useState('idle');    // 'idle' | 'en_cours' | 'terminee'

  // GPS
  const [positions, setPositions]   = useState([]);
  const [geoError,  setGeoError]    = useState(null);
  const [precision, setPrecision]   = useState(null);
  const watchIdRef = useRef(null);

  // Stats
  const [duree,     setDuree]     = useState(0); // en secondes
  const [distance,  setDistance]  = useState(0); // en mètres
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Photos
  const [photos,    setPhotos]    = useState([]);
  const fileInputRef = useRef(null);

  // ── Calcul distance entre deux points GPS (Haversine) ──
  const calcDistance = (p1, p2) => {
    const R = 6371000;
    const dLat = (p2.lat - p1.lat) * Math.PI / 180;
    const dLng = (p2.lng - p1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 +
              Math.cos(p1.lat * Math.PI/180) *
              Math.cos(p2.lat * Math.PI/180) *
              Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  // ── Démarrer ronde GPS ──
  const demarrerGPS = () => {
    setMode('gps');
    setStatut('en_cours');
    setPositions([]);
    setDistance(0);
    setDuree(0);
    setGeoError(null);
    startTimeRef.current = Date.now();

    // Timer durée
    timerRef.current = setInterval(() => {
      setDuree(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    // Suivi GPS continu
    if (!navigator.geolocation) {
      setGeoError("Géolocalisation non supportée.");
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const newPos = {
          lat:       pos.coords.latitude,
          lng:       pos.coords.longitude,
          precision: pos.coords.accuracy,
          timestamp: new Date().toISOString(),
        };
        setPrecision(Math.round(pos.coords.accuracy));
        setPositions(prev => {
          const updated = [...prev, newPos];
          if (prev.length > 0) {
            const d = calcDistance(prev[prev.length - 1], newPos);
            setDistance(old => old + d);
          }
          return updated;
        });
      },
      (err) => {
        const msgs = {
          1: "Permission GPS refusée.",
          2: "Signal GPS indisponible.",
          3: "Délai GPS dépassé.",
        };
        setGeoError(msgs[err.code] || "Erreur GPS.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // ── Démarrer ronde Photo ──
  const demarrerPhoto = () => {
    setMode('photo');
    setStatut('en_cours');
    setPhotos([]);
    setDuree(0);
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setDuree(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  };

  // ── Arrêter la ronde ──
  const arreter = () => {
    setStatut('terminee');
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    // TODO: axios.post('/api/rondes', { positions, photos, duree, distance })
  };

  // ── Nouvelle ronde ──
  const nouvelleRonde = () => {
    setMode('accueil');
    setStatut('idle');
    setPositions([]);
    setPhotos([]);
    setDuree(0);
    setDistance(0);
    setGeoError(null);
  };

  // ── Prendre photo ──
  const prendrePhoto = () => fileInputRef.current?.click();

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const now = new Date();
    setPhotos(prev => [...prev, {
      url,
      file,
      time: `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`,
    }]);
    e.target.value = '';
  };

  const supprimerPhoto = (i) => {
    setPhotos(prev => prev.filter((_, idx) => idx !== i));
  };

  // ── Nettoyage au démontage ──
  useEffect(() => {
    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // ── Formatage durée ──
  const formatDuree = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}h${String(m).padStart(2,'0')}m`
      : `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  };

  const formatDistance = (m) => m >= 1000 ? `${(m/1000).toFixed(2)} km` : `${Math.round(m)} m`;

  // ─────────────────────────────────────────────────────────────
  // RENDU
  // ─────────────────────────────────────────────────────────────

  return (
    <>
      {/* Header */}
      <div className="vv-page-header">
        <div>
          <div className="vv-page-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--pg-blue)" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            Mode Ronde
          </div>
          <div className="vv-page-sub">
            {statut === 'idle'      && 'Sélectionnez un mode de ronde'}
            {statut === 'en_cours'  && (
              <span style={{ color: 'var(--pg-green)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--pg-green)', display: 'inline-block', animation: 'pgPulse 1.5s infinite' }}></span>
                Ronde en cours — {mode === 'gps' ? 'Suivi GPS actif' : 'Mode photo'}
              </span>
            )}
            {statut === 'terminee'  && 'Ronde terminée — résumé ci-dessous'}
          </div>
        </div>

        {/* Bouton arrêter pendant la ronde */}
        {statut === 'en_cours' && (
          <button className="vv-btn vv-btn-danger" onClick={arreter} style={{ flexShrink: 0 }}>
            <StopIcon /> Terminer la ronde
          </button>
        )}
      </div>

      <div className="vv-content">

        {/* ══ ÉCRAN ACCUEIL ══ */}
        {mode === 'accueil' && (
          <div className="vv-panel">
            <RondeAccueil
              onStart={demarrerGPS}
              onPhotoOnly={demarrerPhoto}
            />
          </div>
        )}

        {/* ══ MODE GPS ══ */}
        {mode === 'gps' && (
          <>
            {/* Stats en temps réel */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
              <StatCard icon={<ClockIcon />}    label="Durée"     value={formatDuree(duree)}       color="var(--pg-blue)" />
              <StatCard icon={<FootprintIcon />} label="Distance"  value={formatDistance(distance)}  color="var(--pg-green)" />
              <StatCard icon={<MapPinIcon />}    label="Points GPS" value={positions.length}          color="var(--pg-amber)" />
            </div>

            {/* Erreur GPS */}
            {geoError && (
              <div className="vv-alert danger" style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', fontSize: '13px', color: '#f87171' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {geoError}
              </div>
            )}

            {/* Précision GPS */}
            {precision && !geoError && (
              <div style={{ fontSize: '11px', color: precision < 20 ? 'var(--pg-green)' : precision < 50 ? 'var(--pg-amber)' : 'var(--pg-red)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }}></span>
                Précision GPS : ±{precision}m {precision < 20 ? '(excellente)' : precision < 50 ? '(bonne)' : '(faible)'}
              </div>
            )}

            {/* Carte */}
            <div className="vv-panel" style={{ padding: '1rem' }}>
              <div style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--pg-text-muted)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MapPinIcon /> Carte du trajet en temps réel
              </div>
              {positions.length === 0 && statut === 'en_cours' && (
                <div style={{ textAlign: 'center', padding: '1rem', fontSize: '12px', color: 'var(--pg-text-muted)' }}>
                  Acquisition du signal GPS…
                </div>
              )}
              <RondeMap positions={positions} isTracking={statut === 'en_cours'} />
            </div>

            {/* Photos pendant GPS */}
            <div className="vv-panel" style={{ padding: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--pg-text-muted)' }}>
                  Photos de la ronde
                </div>
                <button className="vv-btn vv-btn-warn" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={prendrePhoto}>
                  <CameraIcon /> Prendre une photo
                </button>
              </div>
              <PhotoGallery photos={photos} onDelete={supprimerPhoto} />
              {photos.length === 0 && (
                <div style={{ textAlign: 'center', padding: '1rem', fontSize: '12px', color: 'var(--pg-text-muted)', border: '2px dashed var(--pg-border)', borderRadius: '10px', cursor: 'pointer' }} onClick={prendrePhoto}>
                  Appuyez ici pour prendre une photo
                </div>
              )}
            </div>
          </>
        )}

        {/* ══ MODE PHOTO SEUL ══ */}
        {mode === 'photo' && statut === 'en_cours' && (
          <>
            <StatCard icon={<ClockIcon />} label="Durée de la ronde" value={formatDuree(duree)} color="var(--pg-amber)" />

            <div className="vv-panel" style={{ padding: '1.5rem', textAlign: 'center' }}>
              <button
                onClick={prendrePhoto}
                style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: 'rgba(245,166,35,0.1)',
                  border: '2px solid rgba(245,166,35,0.3)',
                  color: 'var(--pg-amber)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 12px',
                  transition: 'all 0.2s',
                  fontSize: '13px',
                }}
                onMouseOver={e => e.currentTarget.style.background = 'rgba(245,166,35,0.2)'}
                onMouseOut={e => e.currentTarget.style.background = 'rgba(245,166,35,0.1)'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round" width="36" height="36">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </button>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1rem', fontWeight: 700, color: 'var(--pg-text)', marginBottom: '4px' }}>
                Prendre une photo
              </div>
              <div style={{ fontSize: '12px', color: 'var(--pg-text-muted)' }}>
                {photos.length} photo{photos.length !== 1 ? 's' : ''} prise{photos.length !== 1 ? 's' : ''}
              </div>
            </div>

            <div className="vv-panel" style={{ padding: '1rem' }}>
              <PhotoGallery photos={photos} onDelete={supprimerPhoto} />
              {photos.length === 0 && (
                <div style={{ textAlign: 'center', padding: '1.5rem', fontSize: '12px', color: 'var(--pg-text-muted)' }}>
                  Aucune photo pour le moment
                </div>
              )}
            </div>
          </>
        )}

        {/* ══ RÉSUMÉ FIN DE RONDE ══ */}
        {statut === 'terminee' && (
          <div className="vv-panel" style={{ animation: 'vvSlideDown 0.4s ease' }}>

            {/* Badge succès */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', border: '2px solid var(--pg-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', color: 'var(--pg-green)' }}>
                <CheckIcon />
              </div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1.1rem', fontWeight: 700, color: 'var(--pg-text)' }}>
                Ronde terminée
              </div>
              <div style={{ fontSize: '12px', color: 'var(--pg-text-muted)', marginTop: '3px' }}>
                Mode : {mode === 'gps' ? 'Suivi GPS + Photos' : 'Photos uniquement'}
              </div>
            </div>

            {/* Stats finales */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '1.25rem' }}>
              <StatCard icon={<ClockIcon />}     label="Durée totale"  value={formatDuree(duree)}      color="var(--pg-blue)" />
              {mode === 'gps' && (
                <StatCard icon={<FootprintIcon />} label="Distance"     value={formatDistance(distance)} color="var(--pg-green)" />
              )}
              {mode === 'gps' && (
                <StatCard icon={<MapPinIcon />}    label="Points GPS"   value={positions.length}         color="var(--pg-amber)" />
              )}
              <StatCard icon={<CameraIcon />}    label="Photos"        value={photos.length}            color="var(--pg-amber)" />
            </div>

            {/* Galerie finale */}
            {photos.length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <PhotoGallery photos={photos} onDelete={() => {}} />
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid var(--pg-border)', paddingTop: '1rem' }}>
              <button className="vv-btn vv-btn-ghost" onClick={nouvelleRonde}>
                Nouvelle ronde
              </button>
              <button className="vv-btn vv-btn-primary" onClick={() => {
                // TODO: axios.post('/api/rondes', { positions, photos, duree, distance, mode })
                alert('Ronde enregistrée ! (à connecter au backend)');
              }}>
                <CheckIcon /> Enregistrer la ronde
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Input photo caché */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handlePhoto}
      />
    </>
  );
}